import React, { useRef } from 'react';
import styled from 'styled-components/macro';
import { select } from 'd3-selection';
import { axisLeft, axisBottom } from 'd3-axis';

import formatDate from 'helpers/formatDate';
import formatNumber from 'helpers/formatNumber';

import colors from 'styles/themes/colors';
import { marginType } from './DoubleLineChart';

const HorizontalAxeContainer = styled('g')<any>`
  line {
    stroke: ${colors.Grey};
  }
  & > g:first-of-type {
    line {
      display: none;
    }
  }
  text {
    font-size: 9px;
    fill: ${({ theme }): string => theme.CasperGrey};
    transform: translate(30px, 7px);

    @media screen and (min-width: 40em) {
      font-size: 11px;
    }
  }
`;

const VerticalAxeContainer = styled('g')<any>`
  & > g:last-of-type {
    line {
      display: ${({ isPosition }): string => (isPosition ? 'none' : 'inline')};
    }
  }
  line {
    stroke: ${colors.Grey};
    stroke-dasharray: 3;
  }
`;

interface IAxes {
  label: string;
  width: number;
  yScale: any;
}
interface IHorizontalAxe extends IAxes {
  height: number;
  margin: marginType;
  offset: number;
  xScale: any;
  xScaleValues: any[];
  showHorizontalAxe?: boolean;
}

export const HorizontalAxe: React.FC<IHorizontalAxe> = (props) => {
  const {
    height,
    label,
    margin,
    offset,
    width,
    xScale,
    xScaleValues,
    yScale,
    showHorizontalAxe,
  } = props;

  const axeElement = useRef(null);

  if (!yScale || !yScale.hasOwnProperty('ticks')) return <g />;

  const isPosition = label === 'Position';
  const ticksHeight = isPosition
    ? -height + 10
    : offset === 0
    ? height - 10
    : offset - 2 * 20;
  const axe = axisBottom(xScale)
    .tickSize(ticksHeight)
    .tickValues(xScaleValues)
    .tickFormat((d: any) => formatDate(new Date(d), 'day'));
  select(axeElement.current).call(axe as any);

  return (
    <>
      {showHorizontalAxe && (
        <>
          {/* horizontal axe line */}
          <g transform={`translate(0, ${height - margin.topHorizontalAxe})`}>
            <line stroke={colors.CasperGrey} x2={width} />
          </g>

          {/* horizontal ticks and their grid */}
          <HorizontalAxeContainer
            ref={axeElement}
            transform={`translate(0, ${
              isPosition ? height - margin.topHorizontalAxe : offset
            })`}
          />
        </>
      )}
    </>
  );
};

export const VerticalAxe: React.FC<IAxes> = (props) => {
  const { label, width, yScale } = props;

  const axeElement = useRef(null);
  const isPosition = label === 'Position';

  if (!yScale || !yScale.hasOwnProperty('ticks')) return <g />;

  const formatValue = (d: any, isPosition: boolean): any =>
    isPosition ? d : formatNumber(d, 'number', true).replace(/[a-zA-Z]$/, '');

  const ticks = yScale.ticks().filter((d: any) => {
    const value = formatValue(d, isPosition);
    return isPosition && Number(value) % 1 ? '' : value;
  });
  const axis = axisLeft(yScale)
    .tickSize(-width)
    .tickFormat((d: any) => formatValue(d, isPosition))
    /** In case of axis for Position we add 1 to get a tick of value 1 */
    .tickValues(ticks);
  select(axeElement.current)
    .style('font-size', '11px')
    .call(axis as any);

  return (
    <>
      {/* vertical axe line, its ticks and their grid */}
      <VerticalAxeContainer ref={axeElement} isPosition={isPosition} />
    </>
  );
};
